import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import { getDocuments, deleteDocument, createDocument, updateDocument, getDocument } from '../../api/adminService';

interface Document {
  id: string;
  title: string;
  author: string;
  source: string;
  chunks_count: number;
  preview: string;
}

const DocumentList = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    source: '',
  });

  useEffect(() => {
    loadDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await getDocuments();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      showSnackbar('Error al cargar documentos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
      return;
    }

    try {
      await deleteDocument(docId);
      showSnackbar('Documento eliminado exitosamente', 'success');
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      showSnackbar('Error al eliminar documento', 'error');
    }
  };

  const handleCreate = async () => {
    try {
      await createDocument(formData);
      showSnackbar('Documento creado exitosamente', 'success');
      setOpenDialog(false);
      resetForm();
      loadDocuments();
    } catch (error) {
      console.error('Error creating document:', error);
      showSnackbar('Error al crear documento', 'error');
    }
  };

  const handleEdit = async () => {
    if (!selectedDoc) return;

    try {
      await updateDocument(selectedDoc.id, {
        content: formData.content,
        author: formData.author,
        source: formData.source,
      });
      showSnackbar('Documento actualizado exitosamente', 'success');
      setOpenEditDialog(false);
      resetForm();
      loadDocuments();
    } catch (error) {
      console.error('Error updating document:', error);
      showSnackbar('Error al actualizar documento', 'error');
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const openEditDialogForDoc = async (doc: Document) => {
    setSelectedDoc(doc);
    setLoadingContent(true);
    setOpenEditDialog(true);
    
    try {
      // Cargar el contenido completo del documento
      const fullDoc = await getDocument(doc.id);
      setFormData({
        title: fullDoc.title,
        content: fullDoc.content, // Contenido completo, no solo preview
        author: fullDoc.author,
        source: fullDoc.source,
      });
    } catch (error) {
      console.error('Error loading full document:', error);
      showSnackbar('Error al cargar el contenido completo', 'error');
      // Usar el preview como fallback
      setFormData({
        title: doc.title,
        content: doc.preview,
        author: doc.author,
        source: doc.source,
      });
    } finally {
      setLoadingContent(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      author: '',
      source: '',
    });
    setSelectedDoc(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Documentos en Vector Store
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ borderRadius: 2 }}
        >
          Crear Documento
        </Button>
      </Box>

      {documents.length === 0 ? (
        <Alert severity="info">No hay documentos en la base de datos</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {documents.map((doc) => (
            <Card key={doc.id} sx={{ '&:hover': { boxShadow: 4 }, transition: 'all 0.3s' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DescriptionIcon sx={{ mr: 1, color: '#4CAF50' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {doc.title}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2, 
                        maxHeight: '120px', 
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        p: 1.5,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        lineHeight: 1.6
                      }}
                    >
                      {doc.preview.length > 300 ? doc.preview.substring(0, 300) + '...' : doc.preview}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`Autor: ${doc.author}`} size="small" />
                      <Chip label={`Fuente: ${doc.source}`} size="small" />
                      <Chip label={`${doc.chunks_count} fragmentos`} size="small" color="primary" />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    <IconButton
                      color="primary"
                      onClick={() => openEditDialogForDoc(doc)}
                      sx={{ '&:hover': { bgcolor: 'rgba(52, 152, 219, 0.1)' } }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(doc.id)}
                      sx={{ '&:hover': { bgcolor: 'rgba(231, 76, 60, 0.1)' } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Dialog para crear documento */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Crear Nuevo Documento</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Título"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Contenido"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            margin="normal"
            multiline
            rows={8}
            required
          />
          <TextField
            fullWidth
            label="Autor"
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Fuente"
            value={formData.source}
            onChange={(e) => setFormData({ ...formData, source: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!formData.title || !formData.content}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para editar documento */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar Documento: {selectedDoc?.title}</DialogTitle>
        <DialogContent>
          {loadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Cargando contenido completo...</Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                label="Contenido"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                margin="normal"
                multiline
                rows={8}
                required
              />
              <TextField
                fullWidth
                label="Autor"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Fuente"
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                margin="normal"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button onClick={handleEdit} variant="contained" disabled={!formData.content || loadingContent}>
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentList;
